import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowDownload20Regular,
  ArrowLeft20Regular,
  Delete20Regular,
  Play20Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCancelPublishedDocumentMetadataMigration,
  usePublishedDocumentMetadataMigration,
  usePublishedDocumentMetadataMigrationExceptions,
  usePublishedDocumentMetadataMigrationWorkerStatus,
  useStartPublishedDocumentMetadataMigration,
} from "../hooks/usePublishedDocumentMetadataMigration";
import { ClientRoutes } from "../models/ClientRoutes";
import {
  isMigrationActive,
  PublishedDocumentMetadataMigrationStatus,
} from "../models/PublishedDocumentMetadataMigration";
import { VsaaColorTokens } from "../style/VsaaColorTokens";

const storageKey = "published-document-metadata-migration-job-id";
const useStyles = makeStyles({
  root: {
    padding: tokens.spacingVerticalL,
    overflowY: "auto",
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  panel: {
    marginTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${VsaaColorTokens.color_file_extension_badge_border}`,
    paddingTop: tokens.spacingVerticalL,
  },
  rows: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: tokens.spacingHorizontalL,
  },
  item: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  value: { overflowWrap: "anywhere" },
  progress: { marginTop: tokens.spacingVerticalL },
  danger: {
    backgroundColor: VsaaColorTokens.color_destructive_button_background,
    color: VsaaColorTokens.color_destructive_button_text,
    ":hover": {
      backgroundColor:
        VsaaColorTokens.color_destructive_button_background_hover,
    },
  },
});

const statusKeys: Record<PublishedDocumentMetadataMigrationStatus, string> = {
  0: "pending",
  1: "running",
  2: "completed",
  3: "failed",
  4: "cancelled",
  5: "completedWithExceptions",
  6: "cancelling",
};

type MigrationActionError = {
  key: "startError" | "cancelError" | "downloadError";
  correlationId?: string;
};

type MigrationProblemDetails = {
  correlationId?: string;
};

function parseMigrationProblemDetails(error: unknown): MigrationProblemDetails {
  if (
    !error ||
    typeof error !== "object" ||
    !("message" in error) ||
    typeof error.message !== "string"
  ) {
    return {};
  }

  try {
    const problemDetails: unknown = JSON.parse(error.message);
    if (!problemDetails || typeof problemDetails !== "object") return {};

    const { correlationId } = problemDetails as Record<string, unknown>;
    return {
      correlationId:
        typeof correlationId === "string" &&
        /^[a-zA-Z0-9-]{1,128}$/.test(correlationId)
          ? correlationId
          : undefined,
    };
  } catch {
    return {};
  }
}

function getMigrationActionError(
  error: unknown,
  fallbackKey: MigrationActionError["key"]
): MigrationActionError {
  const problemDetails = parseMigrationProblemDetails(error);
  return {
    key: fallbackKey,
    correlationId: problemDetails.correlationId,
  };
}

export default function PublishedDocumentMetadataMigrationPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobId, setJobId] = useState(
    () => localStorage.getItem(storageKey) ?? undefined
  );
  const [confirmAction, setConfirmAction] = useState<"start" | "cancel" | null>(
    null
  );
  const [actionError, setActionError] = useState<MigrationActionError>();
  const [currentTime, setCurrentTime] = useState(0);
  const statusQuery = usePublishedDocumentMetadataMigration(jobId);
  const workerStatusQuery = usePublishedDocumentMetadataMigrationWorkerStatus();
  const startMutation = useStartPublishedDocumentMetadataMigration();
  const cancelMutation = useCancelPublishedDocumentMetadataMigration(jobId);
  const exceptionDownload =
    usePublishedDocumentMetadataMigrationExceptions(jobId);
  const job = statusQuery.data;
  const workerEnabled = workerStatusQuery.data?.workerEnabled !== false;
  const isActive = isMigrationActive(job?.status);
  const actionPending =
    startMutation.isPending ||
    cancelMutation.isPending ||
    exceptionDownload.isRefetching;

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const start = async (retryExceptions = false, resume = false) => {
    setActionError(undefined);
    try {
      const started = await startMutation.mutateAsync(
        retryExceptions
          ? { resumeJobId: jobId, retryExceptions: true }
          : resume
            ? { resumeJobId: jobId }
            : {}
      );
      localStorage.setItem(storageKey, started.id);
      setJobId(started.id);
      setConfirmAction(null);
    } catch (error) {
      setActionError(getMigrationActionError(error, "startError"));
      setConfirmAction(null);
    }
  };

  const cancel = async () => {
    setActionError(undefined);
    try {
      await cancelMutation.mutateAsync();
      setConfirmAction(null);
    } catch (error) {
      setActionError(getMigrationActionError(error, "cancelError"));
      setConfirmAction(null);
    }
  };

  const download = async () => {
    setActionError(undefined);
    try {
      const result = await exceptionDownload.refetch();
      if (!result.data) {
        setActionError({ key: "downloadError" });
        return;
      }
      const url = URL.createObjectURL(result.data.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.data.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setActionError(getMigrationActionError(error, "downloadError"));
    }
  };

  const counters = job?.migrationCounters;
  const heartbeatTime = job?.heartbeatAtUtc
    ? new Date(job.heartbeatAtUtc).getTime()
    : 0;
  const stale = Boolean(
    currentTime && heartbeatTime && currentTime - heartbeatTime > 15_000
  );

  return (
    <main className={classes.root} aria-live="polite">
      <div className={classes.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        >
          {t("publishedDocumentMetadataMigration.back")}
        </Button>
        <Text as="h1" size={600} weight="semibold">
          {t("publishedDocumentMetadataMigration.title")}
        </Text>
        <div className={classes.actions}>
          {!isActive && (
            <Button
              appearance="primary"
              icon={<Play20Regular />}
              disabled={actionPending}
              onClick={() => setConfirmAction("start")}
            >
              {t("publishedDocumentMetadataMigration.start")}
            </Button>
          )}
          {isActive && (
            <Button
              className={classes.danger}
              icon={<Delete20Regular />}
              disabled={actionPending}
              onClick={() => setConfirmAction("cancel")}
            >
              {t("publishedDocumentMetadataMigration.cancel")}
            </Button>
          )}
          {(counters?.exceptions ?? 0) > 0 && (
            <Button
              icon={<ArrowDownload20Regular />}
              disabled={actionPending}
              onClick={download}
            >
              {t("publishedDocumentMetadataMigration.export")}
            </Button>
          )}
          {job?.canRetryExceptions && (
            <Button disabled={actionPending} onClick={() => start(true)}>
              {t("publishedDocumentMetadataMigration.retry")}
            </Button>
          )}
          {job?.canResume && !job.canRetryExceptions && !isActive && (
            <Button disabled={actionPending} onClick={() => start(false, true)}>
              {t("publishedDocumentMetadataMigration.resume")}
            </Button>
          )}
        </div>
      </div>

      {!navigator.onLine && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {t("publishedDocumentMetadataMigration.offline")}
          </MessageBarBody>
        </MessageBar>
      )}
      {!workerEnabled && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {t("publishedDocumentMetadataMigration.workerDisabled")}
          </MessageBarBody>
        </MessageBar>
      )}
      {statusQuery.error && (
        <MessageBar intent="error">
          <MessageBarBody>
            {t("publishedDocumentMetadataMigration.loadError")}
          </MessageBarBody>
        </MessageBar>
      )}
      {actionError && (
        <div role="alert">
          <MessageBar intent="error">
            <MessageBarBody>
              {t(`publishedDocumentMetadataMigration.${actionError.key}`)}
              {actionError.correlationId && (
                <>
                  {" "}
                  {t("publishedDocumentMetadataMigration.correlationId")}{" "}
                  {actionError.correlationId}
                </>
              )}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}
      {!job && statusQuery.isLoading && (
        <div className={classes.panel}>
          <Spinner label={t("publishedDocumentMetadataMigration.loading")} />
        </div>
      )}
      {!job && !statusQuery.isLoading && !statusQuery.error && (
        <div className={classes.panel}>
          <Text>{t("publishedDocumentMetadataMigration.empty")}</Text>
        </div>
      )}
      {job && (
        <section className={classes.panel}>
          <div className={classes.rows}>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.status")}</Text>
              <Badge>
                {t(
                  `publishedDocumentMetadataMigration.states.${statusKeys[job.status]}`
                )}
              </Badge>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.jobId")}</Text>
              <Text className={classes.value}>{job.id}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.started")}</Text>
              <Text>{new Date(job.startTime).toLocaleString("sv-SE")}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.heartbeat")}</Text>
              <Text>
                {job.heartbeatAtUtc
                  ? new Date(job.heartbeatAtUtc).toLocaleString("sv-SE")
                  : "-"}
              </Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.page")}</Text>
              <Text>{job.pageNumber}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.scanned")}</Text>
              <Text>{counters?.scanned ?? 0}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.updated")}</Text>
              <Text>{counters?.updated ?? 0}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.skipped")}</Text>
              <Text>{counters?.skipped ?? 0}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.missing")}</Text>
              <Text>{counters?.fileMissing ?? 0}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.ambiguous")}</Text>
              <Text>{counters?.ambiguous ?? 0}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.exceptions")}</Text>
              <Text>{counters?.exceptions ?? 0}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedDocumentMetadataMigration.conflicts")}</Text>
              <Text>{counters?.conflicts ?? 0}</Text>
            </div>
            {!isActive && job.endTime && (
              <div className={classes.item}>
                <Text>{t("publishedDocumentMetadataMigration.finished")}</Text>
                <Text>{new Date(job.endTime).toLocaleString("sv-SE")}</Text>
              </div>
            )}
          </div>
          <ProgressBar
            className={classes.progress}
            value={isActive ? undefined : 1}
            aria-label={t("publishedDocumentMetadataMigration.progress")}
          />
          {stale && (
            <MessageBar intent="warning">
              <MessageBarBody>
                {t("publishedDocumentMetadataMigration.stale")}
              </MessageBarBody>
            </MessageBar>
          )}
          {job.errorMessage && (
            <MessageBar intent="error">
              <MessageBarBody>{job.errorMessage}</MessageBarBody>
            </MessageBar>
          )}
        </section>
      )}

      <Dialog
        open={confirmAction !== null}
        onOpenChange={(_, data) => !data.open && setConfirmAction(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t(
                `publishedDocumentMetadataMigration.confirm.${confirmAction ?? "start"}.title`
              )}
            </DialogTitle>
            <DialogContent>
              {t(
                `publishedDocumentMetadataMigration.confirm.${confirmAction ?? "start"}.body`
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmAction(null)}>
                {t("publishedDocumentMetadataMigration.close")}
              </Button>
              <Button
                appearance="primary"
                disabled={actionPending}
                className={
                  confirmAction === "cancel" ? classes.danger : undefined
                }
                onClick={() =>
                  confirmAction === "cancel" ? cancel() : start()
                }
              >
                {t("publishedDocumentMetadataMigration.confirmAction")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </main>
  );
}
